const VALID_TYPES = ['image', 'video'];
const VALID_ANSWERS = ['ai', 'not-ai'];
const MEDIUM_SCORE_MIN = 30;
const HARD_SCORE_MIN = 70;
let mediaItemsPromise = null;
let preparedInitialMediaPlan = null;

export async function loadMediaItems() {
  if (mediaItemsPromise) {
    return mediaItemsPromise;
  }

  mediaItemsPromise = fetchMediaItems().catch((error) => {
    mediaItemsPromise = null;
    throw error;
  });

  return mediaItemsPromise;
}

async function fetchMediaItems() {
  const response = await fetch('/media.json', {
    cache: 'no-store',
  });

  if (!response.ok) {
    throw new Error('Could not load media.json');
  }

  const mediaItems = await response.json();

  if (!Array.isArray(mediaItems)) {
    throw new Error('media.json must contain an array');
  }

  return mediaItems.filter(
    (item) =>
      item &&
      typeof item.id === 'string' &&
      typeof item.src === 'string' &&
      VALID_TYPES.includes(item.type) &&
      VALID_ANSWERS.includes(item.answer)
  );
}

export function shuffleMediaItems(mediaItems) {
  return [...mediaItems].sort(() => Math.random() - 0.5);
}

export function getNormalizedDifficulty(difficulty) {
  return String(difficulty || '').trim().toLowerCase();
}

export function getTargetDifficulty(score) {
  if (score >= HARD_SCORE_MIN) {
    return 'hard';
  }

  if (score >= MEDIUM_SCORE_MIN) {
    return 'medium';
  }

  return 'easy';
}

export function buildDifficultyProgression(mediaItems, score = 0, preferredDifficulty) {
  const groups = mediaItems.reduce(
    (acc, mediaItem) => {
      const difficulty = getNormalizedDifficulty(mediaItem.difficulty);

      if (difficulty === 'easy' || difficulty === 'medium' || difficulty === 'hard') {
        acc[difficulty].push(mediaItem);
      } else {
        acc.other.push(mediaItem);
      }

      return acc;
    },
    { easy: [], medium: [], hard: [], other: [] }
  );

  const targetDifficulty = getNormalizedDifficulty(preferredDifficulty || getTargetDifficulty(score));
  const fallbackOrder = {
    easy: ['easy', 'medium', 'hard', 'other'],
    medium: ['medium', 'hard', 'easy', 'other'],
    hard: ['hard', 'medium', 'easy', 'other'],
  };
  const difficultyOrder = fallbackOrder[targetDifficulty] || fallbackOrder.easy;

  return difficultyOrder.flatMap((difficulty) => shuffleMediaItems(groups[difficulty]));
}

function getSeenMediaSignature(seenMediaIds) {
  return [...seenMediaIds].sort().join('|');
}

function getInitialMediaPlanKey(deviceId, seenMediaIds, score = 0) {
  return `${deviceId}:${score}:${getSeenMediaSignature(seenMediaIds)}`;
}

export async function prepareInitialMediaPlan(deviceId, seenMediaIds, score = 0) {
  const planKey = getInitialMediaPlanKey(deviceId, seenMediaIds, score);

  if (preparedInitialMediaPlan?.key === planKey) {
    return preparedInitialMediaPlan;
  }

  const playableMediaItems = (await loadMediaItems()).filter(
    (mediaItem) => !seenMediaIds.has(mediaItem.id)
  );
  const mediaItems = buildDifficultyProgression(playableMediaItems, score);

  preparedInitialMediaPlan = {
    key: planKey,
    playableMediaItems,
    mediaItems,
  };

  return preparedInitialMediaPlan;
}

export function consumeInitialMediaPlan(deviceId, seenMediaIds, score = 0) {
  const planKey = getInitialMediaPlanKey(deviceId, seenMediaIds, score);

  if (preparedInitialMediaPlan?.key !== planKey) {
    return null;
  }

  const plan = preparedInitialMediaPlan;
  preparedInitialMediaPlan = null;

  return plan;
}
